import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVideoPlayer } from '../context/VideoPlayerContext';
import { Play, Pause, X, Maximize2, Volume2, VolumeX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MiniPlayer() {
  const navigate = useNavigate();
  const {
    activeVideo, isPlaying, showMiniPlayer, isMiniPlayer,
    closeMiniPlayer, togglePlay, exitMiniPlayer, volume, isMuted,
    setVolume, setIsMuted, updateTime, updateDuration, setIsPlaying,
    currentTime, duration,
  } = useVideoPlayer();

  const miniVideoRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });

  // Sync play/pause state with video element
  useEffect(() => {
    const v = miniVideoRef.current;
    if (!v || !showMiniPlayer || !activeVideo) return;

    // Set the video source if not already set - use filename for stream URL
    const videoFilename = activeVideo.filename || activeVideo.id;
    const src = activeVideo.video_url || `${API}/stream/video/${videoFilename}`;
    if (!v.src || !v.src.includes(videoFilename)) {
      v.src = src;
      v.currentTime = currentTime || 0;
    }

    if (isPlaying) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isPlaying, showMiniPlayer, activeVideo, currentTime]);

  // Sync volume
  useEffect(() => {
    const v = miniVideoRef.current;
    if (!v) return;
    v.volume = volume;
    v.muted = isMuted;
  }, [volume, isMuted]);

  // Handle video events
  const handleTimeUpdate = () => {
    if (miniVideoRef.current) updateTime(miniVideoRef.current.currentTime);
  };
  const handleDurationChange = () => {
    if (miniVideoRef.current) updateDuration(miniVideoRef.current.duration);
  };
  const handleEnded = () => setIsPlaying(false);

  // Expand back to full video page
  const handleExpand = () => {
    if (activeVideo) {
      exitMiniPlayer();
      navigate(`/videos/${activeVideo.id}`);
    }
  };

  // Drag functionality
  const handleDragStart = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    dragStart.current = {
      x: e.clientX, y: e.clientY,
      px: rect.left, py: rect.top,
    };
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPosition({
        x: dragStart.current.px + dx,
        y: dragStart.current.py + dy,
      });
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);

  if (!showMiniPlayer || !activeVideo || !isMiniPlayer) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const style = position.x !== null
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : { right: 24, bottom: 24 };

  return (
    <div
      className="fixed z-[9999] shadow-2xl shadow-black/80 rounded-xl overflow-hidden bg-black border border-[#222] transition-shadow hover:shadow-[#00CCFF]/20 group/mini"
      style={{ width: 360, ...style }}
      onMouseDown={handleDragStart}
      data-testid="mini-player"
    >
      {/* Video Area */}
      <div className="relative aspect-video bg-black cursor-grab active:cursor-grabbing">
        <video
          ref={miniVideoRef}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleDurationChange}
          onEnded={handleEnded}
          playsInline
        />

        {/* Overlay controls - visible on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 opacity-0 group-hover/mini:opacity-100 transition-opacity flex flex-col justify-between p-2">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleExpand}
              className="p-1.5 bg-black/60 hover:bg-[#00CCFF]/30 rounded-full transition-colors"
              data-testid="mini-player-expand"
              title="Expand"
            >
              <Maximize2 size={14} className="text-white" />
            </button>
            <button
              onClick={closeMiniPlayer}
              className="p-1.5 bg-black/60 hover:bg-red-500/40 rounded-full transition-colors"
              data-testid="mini-player-close"
              title="Close"
            >
              <X size={14} className="text-white" />
            </button>
          </div>

          {/* Center play/pause */}
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              className="p-3 bg-black/60 hover:bg-[#00CCFF]/30 rounded-full transition-colors"
              data-testid="mini-player-playpause"
            >
              {isPlaying ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white" />}
            </button>
          </div>

          {/* Bottom bar - volume */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
              className="p-1 text-white/80 hover:text-white"
              data-testid="mini-player-mute"
            >
              {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#333]">
          <div className="h-full bg-[#00CCFF] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Info bar */}
      <div className="px-3 py-2 bg-[#0a0a0a] flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-white text-xs font-['Chakra_Petch'] truncate">{activeVideo.title}</p>
          <p className="text-[#888] text-[10px] font-mono truncate">{activeVideo.artist}</p>
        </div>
        <span className="text-[#00CCFF] text-[9px] font-mono bg-[#00CCFF]/10 px-1.5 py-0.5 rounded-full flex-shrink-0">VIP</span>
      </div>
    </div>
  );
}
